import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

/**
 * Utility functions for handling keyboard events in table and form contexts
 */

/**
 * Checks if a keyboard event should be ignored because it's intended for input fields
 */
export function shouldIgnoreKeyboardEvent(event: KeyboardEvent | ReactKeyboardEvent): boolean {
  const target = event.target as HTMLElement;
  
  if (!target) {
    return false;
  }

  // Check if the target or any of its parents is an input field
  return isInputElement(target);
}

/**
 * Checks if an element is an input field or within an input context
 */
export function isInputElement(element: Element | HTMLElement | null): boolean {
  if (!element) {
    return false;
  }

  // Direct input elements
  if (
    element.tagName === 'INPUT' ||
    element.tagName === 'TEXTAREA' ||
    element.tagName === 'SELECT'
  ) {
    return true;
  }

  // Contenteditable elements
  if (
    element.getAttribute('contenteditable') === 'true' ||
    (element as HTMLElement).isContentEditable
  ) {
    return true;
  }

  // ARIA roles that indicate text input
  const role = element.getAttribute('role');
  if (role === 'textbox' || role === 'combobox' || role === 'listbox' || role === 'option') {
    return true;
  }

  // Elements with specific data attributes or classes that indicate input behavior
  if (
    element.hasAttribute('aria-multiline') ||
    element.closest('[contenteditable="true"]') ||
    element.closest('[data-input-wrapper]') ||
    element.closest('[data-task-name-input]') ||
    element.closest('[data-radix-select-content]') ||
    element.closest('[data-radix-popover-content]') ||
    element.closest('[data-radix-dropdown-menu-content]') ||
    element.closest('[data-radix-context-menu-content]') ||
    element.closest('.input-field') ||
    element.closest('.editable-cell') ||
    element.closest('[data-editing="true"]')
  ) {
    return true;
  }

  return false;
}

/**
 * Prevents keyboard event handling for table/drag operations when user is typing in input fields
 */
export function createInputAwareKeyboardHandler<T extends KeyboardEvent | ReactKeyboardEvent>(
  handler: (event: T) => void
) {
  return (event: T) => {
    if (!shouldIgnoreKeyboardEvent(event)) {
      handler(event);
    }
  };
} 
